import { Role } from '@ywwwtseng/ywjs';
import {
  pgTable,
  timestamp,
  text,
  varchar,
  boolean,
  integer,
  numeric,
  jsonb,
  date,
  uuid,
  index,
  uniqueIndex,
  serial,
  primaryKey,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { relations } from 'drizzle-orm';
import {
  roles,
  networks,
  withdraw_intent_status,
  transaction_status,
  right_assignment_status,
  gift_types,
  amount_types,
  token_status,
  gift_status,
  ad_activity_status,
  ad_providers,
  ad_sources,
} from './enum';

export const users = pgTable('users', {
  id: varchar({ length: 100 }).primaryKey(),
  tg_first_name: varchar({ length: 100 }).notNull(),
  tg_last_name: varchar({ length: 100 }),
  tg_username: varchar({ length: 100 }),
  tg_photo_url: varchar({ length: 255 }),
  first_name: varchar({ length: 100 }),
  last_name: varchar({ length: 100 }),
  birth_date: date('birth_date'),
  gender: varchar({
    length: 10,
    enum: ['male', 'female', 'non_binary', 'other'],
  }),

  invited_by: varchar({ length: 100 }),
  language_code: varchar({ length: 25 }),
  timezone: varchar({ length: 25 }),
  can_withdraw: boolean().notNull().default(true),
  role: varchar({
    length: 10,
    // root: 超級管理員
    // admin: 管理員
    // operator: 營運
    // user: 一般用戶
    enum: roles,
  })
    .notNull()
    .default(Role.USER_TG),
  notification: boolean().notNull().default(true),
  blacklisted: boolean().notNull().default(false),
  activity_score: integer().notNull().default(3), // User activity score, default is 3
  fission_moonini_total: numeric({ precision: 78 }).notNull().default('0'), // 三種裂變兌換的 $MOONINI 總和（系統分享獎勵、系統分享領取獎勵、群裂變分享獎勵）

  // unique
  email: varchar({ length: 255 }).unique(),

  eth_address: varchar({ length: 100 }).unique(),
  sol_address: varchar({ length: 100 }).unique(),
  ton_address: varchar({ length: 100 }).unique(),
  tron_address: varchar({ length: 100 }).unique(),
  btc_address: varchar({ length: 100 }).unique(),
  bsc_address: varchar({ length: 100 }).unique(),

  // bot 推送訊息時間
  bot_at: timestamp('bot_at', { withTimezone: true }),

  // withdraw 次數
  withdraw_count: integer().notNull().default(0),
  withdraw_usd: numeric({ precision: 78, scale: 6 }).notNull().default('0'),

  // deposit 次數
  deposit_count: integer().notNull().default(0),
  // deposit 歷史總充值 (USD)
  deposit_usd: numeric({ precision: 78, scale: 6 }).notNull().default('0'),

  // 淨本金 (Net Deposit) = 歷史總充值 - 歷史總提領
  net_deposit: numeric({ precision: 78, scale: 6 }).notNull().default('0'),

  // 徽章等級 (1: 地球旅人, 2: 月球漫步者, 3: 星際領航員)
  badge_level: integer().notNull().default(1),

  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
  updated_at: timestamp('updated_at', { withTimezone: true })
    .notNull()
    .$onUpdate(() => new Date()),
});

export const users_relations = relations(users, ({ one }) => ({
  invitee: one(users, {
    fields: [users.invited_by],
    references: [users.id],
  }),
}));

export const tokens = pgTable('tokens', {
  id: varchar({ length: 100 }).primaryKey(),
  symbol: varchar({ length: 25 }).notNull(),
  network: varchar({
    length: 25,
    enum: networks,
  }),
  name: varchar({ length: 50 }).notNull(),
  icon: varchar({ length: 255 }).notNull(),
  decimals: integer().notNull().default(18),
  status: varchar({ length: 10, enum: token_status })
    .notNull()
    .default(token_status[0]),
  token_address: varchar({ length: 255 }),
  token_program: varchar({ length: 255 }),
  usd_price: varchar({ length: 255 }),
  order: serial('order').notNull(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
  updated_at: timestamp('updated_at', { withTimezone: true })
    .$onUpdate(() => new Date()),
});

export const user_balances = pgTable(
  'user_balances',
  {
    user_id: varchar({ length: 100 })
      .notNull()
      .references(() => users.id),
    token_id: varchar({ length: 100 })
      .notNull()
      .references(() => tokens.id),
    amount: numeric({ precision: 78 }).notNull().default('0'),
    /** 鎖定數量（發禮/待處理等），可用餘額 = amount - locked_amount，提領與發禮須參考此欄位 */
    locked_amount: numeric({ precision: 78 }).notNull().default('0'),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.user_id, table.token_id] }),
    check('amount_gte_zero', sql`${table.amount} >= 0`),
    check('locked_amount_gte_zero', sql`${table.locked_amount} >= 0`),
    check('amount_gte_locked', sql`${table.amount} >= ${table.locked_amount}`),
  ]
);

// tg_chats
export const tg_chats = pgTable('tg_chats', {
  id: varchar({ length: 25 }).primaryKey(),
  name: varchar({ length: 100 }).notNull(),
  username: varchar({ length: 100 }),
  owner_id: varchar({ length: 100 }).references(() => users.id),
  blacklisted: boolean().notNull().default(false),
  allow_guest_gifts: boolean(),
  invite_link: varchar({ length: 255 }),
  gift_count: integer().notNull().default(0),
  member_count: integer().notNull().default(0),
  is_deleted: boolean().notNull().default(false),
  total_sented_usdt: numeric({ precision: 78 }).notNull().default('0'),
  photo_url: varchar({ length: 255 }),
  recent_gift_sent_at: timestamp('recent_gift_sent_at', { withTimezone: true }),
  visible: boolean().notNull().default(true),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
  updated_at: timestamp('updated_at', { withTimezone: true })
    .$onUpdate(() => new Date()),
});

export const chat_admins = pgTable('chat_admins', {
  id: uuid('id').defaultRandom().primaryKey(),
  chat_id: varchar({ length: 25 }).references(() => tg_chats.id),
  user_id: varchar({ length: 100 }).references(() => users.id),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// user_chat_memberships - 记录用户加入 chat 的历史，用于防止 chat growth gift 被洗
export const user_chat_memberships = pgTable(
  'user_chat_memberships',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    chat_id: varchar({ length: 25 })
      .references(() => tg_chats.id)
      .notNull(),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index('idx_user_chat_memberships_user_id').on(table.user_id),
    index('idx_user_chat_memberships_chat_id').on(table.chat_id),
    // 唯一索引：确保同一个用户和 chat 的组合只记录一次
    uniqueIndex('idx_user_chat_memberships_user_chat_unique').on(table.user_id, table.chat_id),
  ]
);

// user_activities
export const user_activities = pgTable('user_activities', {
  id: uuid('id').defaultRandom().primaryKey(),
  user_id: varchar({ length: 100 }).references(() => users.id),
  ip: varchar({ length: 255 }).notNull(),
  user_agent: varchar({ length: 255 }).notNull(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// logs
export const logs = pgTable(
  'logs',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    user_first_name: varchar({ length: 100 }),
    type: varchar({ length: 255 }).notNull(),
    token_id: varchar({ length: 100 }).references(() => tokens.id),
    amount: numeric({ precision: 78 }).notNull().default('0'),
    metadata: jsonb(),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [index('idx_logs_type').on(table.type)]
);

// user activity logs
export const user_activity_logs = pgTable(
  'user_activity_logs',
  {
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    type: varchar({ length: 255 }).notNull(),
    amount: numeric({ precision: 78 }).notNull().default('0'),
    metadata: jsonb(),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [primaryKey({ columns: [table.user_id, table.type] })]
);

/** 廣告歸因：用戶來自哪個廣告來源（AdsGram、Google 等），供轉換回傳使用 */
export const ad_attributions = pgTable(
  'ad_attributions',
  {
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    source: varchar({ length: 50, enum: ad_sources }).notNull(),
    metadata: jsonb().notNull(),
    /** 已回報的 goaltype 列表（1=註冊、2=首次儲值、3=重複），同一 goaltype 只回報一次，避免刷子導致廣告費暴增 */
    reported_goaltypes: jsonb().notNull().default([]),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updated_at: timestamp('updated_at', { withTimezone: true })
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (table) => [
    primaryKey({ columns: [table.user_id, table.source] }),
    // 依 source 查詢（報表／統計）時使用；若僅依 (user_id, source) 查可考慮移除以減少寫入成本
    index('idx_ad_attributions_source').on(table.source),
  ]
);

/** 廣告活動記錄：觀看前建立 pending，廣告商 postback 後更新為 claimed 並發放獎勵 */
export const ad_activities = pgTable(
  'ad_activities',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    provider_id: varchar({ length: 50, enum: ad_providers }).notNull(), // 廣告商標識，如 'gigapub'
    type: varchar({ length: 20 }).notNull(), // 'view' 或 'click'
    status: varchar({ length: 20, enum: ad_activity_status }).notNull().default(ad_activity_status[0]),
    token_id: varchar({ length: 100 }).references(() => tokens.id),
    amount: numeric({ precision: 78 }).notNull().default('0'), // 發放的 MOONINI 數量
    reward_id: varchar({ length: 255 }),
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index('idx_ad_activities_user_created').on(table.user_id, table.created_at),
    index('idx_ad_activities_provider').on(table.provider_id),
    index('idx_ad_activities_status').on(table.status),
  ]
);

// user logs 
export const user_logs = pgTable('user_logs', {
  id: varchar({ length: 255 }).primaryKey(),
  user_id: varchar({ length: 100 })
    .references(() => users.id)
    .notNull(),
  type: varchar({ length: 255 }).notNull(),
  metadata: jsonb(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// missions - 任务定义表
export const missions = pgTable(
  'missions',
  {
    id: varchar({ length: 100 }).primaryKey(), // 例如: invite, follow_x, join_channel
    type: varchar({ length: 50 }).notNull(), // achievement, social, daily
    title_key: varchar({ length: 255 }).notNull(), // i18n key
    description_key: varchar({ length: 255 }).notNull(), // i18n key
    token_id: varchar({ length: 100 }).references(() => tokens.id).notNull(), // 奖励的代币 ID
    amount: numeric({ precision: 78 }).notNull().default('0'), // 奖励数量（以代币的最小单位计算）
    times: integer().notNull().default(1), // 每个用户最多完成次数
    enabled: boolean().notNull().default(true), // 是否启用
    config: jsonb(), // 任务配置（如邀请人数、链接等）
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updated_at: timestamp('updated_at', { withTimezone: true })
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (table) => [
    index('idx_missions_type').on(table.type),
    index('idx_missions_enabled').on(table.enabled),
  ]
);

// app logs
export const app_logs = pgTable('app_logs', {
  type: varchar({ length: 255 }).notNull().primaryKey(),
  amount: numeric({ precision: 78 }).notNull().default('0'),
  metadata: jsonb(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const system_logs = pgTable('system_logs', {
  id: uuid('id').defaultRandom().primaryKey(),
  type: varchar({ length: 255 }).notNull(),
  amount: numeric({ precision: 78 }).notNull().default('0'),
  metadata: jsonb(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// transactions
export const transactions = pgTable('transactions', {
  id: varchar({ length: 255 }).primaryKey(),
  user_id: varchar({ length: 100 }).references(() => users.id),
  token_id: varchar({ length: 100 }).references(() => tokens.id),
  from_address: varchar({ length: 255 }).notNull(),
  to_address: varchar({ length: 255 }).notNull(),
  tx_hash: varchar({ length: 255 }).notNull(),
  status: varchar({ length: 10, enum: transaction_status }).notNull(),
  error_message: varchar({ length: 255 }),
  amount: varchar({ length: 255 }).notNull(),
  metadata: jsonb(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// gifts
export const gifts = pgTable('gifts', {
  id: uuid('id').defaultRandom().primaryKey(),
  type: varchar({ length: 20, enum: gift_types }).notNull(),
  claim_type: varchar({ length: 20 }),
  amount_type: varchar({ length: 20, enum: amount_types }).default(
    amount_types[0]
  ),
  image: varchar({ length: 255 }),
  tg_chat_id: varchar({ length: 25 })
    .references(() => tg_chats.id),
  tg_chat_name: varchar({ length: 100 }),
  tg_chat_username: varchar({ length: 100 }),
  user_first_name: varchar({ length: 100 }),
  description: text().notNull(),
  user_id: varchar({ length: 100 })
    .references(() => users.id)
    .notNull(),
  token_id: varchar({ length: 100 })
    .references(() => tokens.id)
    .notNull(),
  message_id: varchar({ length: 255 }),
  recipients: integer().notNull().default(0),
  balance: numeric({ precision: 78 }).notNull(),
  total: varchar({ length: 255 }).notNull(),
  sented_usdt: numeric({ precision: 78 }).notNull().default('0'),
  refunded: varchar({ length: 255 }),
  platform_fee: varchar({ length: 255 }).notNull(),
  chat_fee: varchar({ length: 255 }).notNull(),
  start_at: timestamp('start_at', { withTimezone: true }),
  end_at: timestamp('end_at', { withTimezone: true }),
  language_code: varchar({ length: 25 }),
  status: varchar({ length: 10, enum: gift_status })
    .notNull()
    .default(gift_status[0]),
  metadata: jsonb().notNull(),
  ctr: integer().notNull().default(0),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
  updated_at: timestamp('updated_at', { withTimezone: true })
    .notNull()
    .$onUpdate(() => new Date()),
});

export const wallets = pgTable('wallets', {
  id: varchar({ length: 100 }).primaryKey(),
  network: varchar({ length: 25, enum: networks }).notNull(),
  address: varchar({ length: 255 }).notNull(),
  encrypted_private_key: text().notNull(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// wallet_bindings - 记录钱包地址绑定历史，确保地址永久绑定限制
export const wallet_bindings = pgTable(
  'wallet_bindings',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    user_id: varchar({ length: 100 })
      .references(() => users.id)
      .notNull(),
    address: varchar({ length: 255 }).notNull(),
    network: varchar({ length: 25, enum: networks }).notNull(),
    field: varchar({ length: 25 }).notNull(), // sol_address, bsc_address, eth_address, etc.
    created_at: timestamp('created_at', { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index('idx_wallet_bindings_address').on(table.address),
    index('idx_wallet_bindings_user_id').on(table.user_id),
    // 唯一索引：确保同一个地址在同一个网络上只能被绑定一次（防止并发竞态）
    uniqueIndex('idx_wallet_bindings_address_network_unique').on(table.address, table.network),
  ]
);

export const right_assignments = pgTable('right_assignments', {
  id: varchar({ length: 255 }).primaryKey(),
  assignor: varchar({ length: 255 }).notNull(),
  assignee: varchar({ length: 255 }).notNull(),
  amount: numeric({ precision: 78 }).notNull().default('0'),
  investment_index: integer().notNull(),
  assign_id: varchar({ length: 255 }).notNull(),
  status: varchar({ length: 10, enum: right_assignment_status }).notNull().default(right_assignment_status[0]),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
  updated_at: timestamp('updated_at', { withTimezone: true })
    .notNull()
    .$onUpdate(() => new Date()),
});

export const withdraw_intents = pgTable('withdraw_intents', {
  id: uuid('id').defaultRandom().primaryKey(),
  user_id: varchar({ length: 100 }).references(() => users.id),
  network: varchar({ length: 25, enum: networks }).notNull(),
  token_id: varchar({ length: 100 }).references(() => tokens.id),
  amount: numeric({ precision: 78 }).notNull().default('0'),
  status: varchar({ length: 20, enum: withdraw_intent_status }).notNull().default(withdraw_intent_status[0]),
  destination: varchar({ length: 255 }),
  risk_score: integer().notNull().default(0),
  message: varchar({ length: 255 }),
  ip: varchar({ length: 255 }),
  tx_hash: varchar({ length: 255 }),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});


export const dangerous_ips = pgTable('dangerous_ips', {
  id: uuid('id').defaultRandom().primaryKey(),
  ip: varchar({ length: 64 }).notNull(),
  reason: varchar({ length: 64 }).notNull(),
  created_at: timestamp('created_at', { withTimezone: true })
    .notNull()
    .defaultNow(),
});
